import {
  type FormBrandSettings,
  getFormBrandSettings,
  getFormLayoutSettings,
} from '@/lib/form-theme'
import { cn } from '@/lib/utils'
import type { PublicForm } from '@/types/form'

export function BrandChrome({
  form,
  contained = false,
}: {
  form: PublicForm
  contained?: boolean
}) {
  const brand = getFormBrandSettings(form.settings)
  const layout = getFormLayoutSettings(form.settings)

  return (
    <>
      {brand.showLogo && <BrandLogo brand={brand} contained={contained} />}
      {brand.showBrandMark && (
        <BrandMark brand={brand} contained={contained} progressPosition={layout.progressPosition} />
      )}
    </>
  )
}

export function BrandBanner({
  form,
  surface,
}: {
  form: PublicForm
  surface: 'welcome' | 'questions' | 'thankyou'
}) {
  const brand = getFormBrandSettings(form.settings)
  const shouldShow =
    brand.showBanner &&
    brand.bannerUrl &&
    (brand.bannerDisplay === 'all' ||
      brand.bannerDisplay === surface ||
      (brand.bannerDisplay === 'questions' && surface === 'questions'))

  if (!shouldShow) return null

  return (
    <div
      className="mb-8 w-full overflow-hidden border shadow-sm"
      style={{
        background: 'color-mix(in srgb, var(--form-bg,#F8FAFC) 78%, white)',
        borderColor: 'color-mix(in srgb, var(--form-accent,#6366F1) 22%, transparent)',
        borderRadius: brand.bannerRadius,
        height: brand.bannerHeight,
      }}
    >
      <div
        className="relative size-full overflow-hidden"
        style={{ borderRadius: brand.bannerRadius }}
      >
        <img
          src={brand.bannerUrl}
          alt={brand.bannerAlt}
          className="size-full"
          style={{
            objectFit: brand.bannerFit,
            objectPosition: `${brand.bannerFocalX}% ${brand.bannerFocalY}%`,
            transform: `scale(${brand.bannerZoom})`,
            transformOrigin: `${brand.bannerFocalX}% ${brand.bannerFocalY}%`,
          }}
        />
        {brand.bannerOverlayOpacity > 0 && (
          <div
            className="absolute inset-0"
            style={{
              background: brand.bannerOverlayColor,
              opacity: brand.bannerOverlayOpacity / 100,
            }}
          />
        )}
      </div>
    </div>
  )
}

function BrandLogo({
  brand,
  contained,
}: {
  brand: FormBrandSettings
  contained: boolean
}) {
  const content = (
    <div
      className={cn(
        'pointer-events-auto flex max-w-[min(80vw,360px)] items-center gap-2.5 px-2.5 py-2 text-sm transition hover:opacity-90',
        brand.logoFrame && 'border shadow-sm backdrop-blur',
      )}
      style={{
        background: brand.logoFrame ? 'color-mix(in srgb, var(--form-bg,#F8FAFC) 82%, white)' : '',
        borderColor: brand.logoFrame
          ? 'color-mix(in srgb, var(--form-accent,#6366F1) 24%, transparent)'
          : '',
        borderRadius: brand.logoFrame ? 'calc(var(--form-radius,8px) + 8px)' : undefined,
        color: 'var(--form-text,#0F172A)',
        fontFamily: 'var(--form-body-font,var(--form-font,Geist))',
      }}
    >
      {brand.logoUrl ? (
        <img
          src={brand.logoUrl}
          alt={brand.logoAlt}
          className={cn(
            'shrink-0 object-contain',
            brand.logoShape === 'circle' && 'rounded-full',
            brand.logoShape === 'rounded' && 'rounded-md',
          )}
          style={{
            height: brand.logoSize,
            maxWidth: brand.logoSize * 3.2,
            width: 'auto',
          }}
        />
      ) : (
        <span
          className={cn(
            'grid shrink-0 place-items-center font-semibold',
            brand.logoShape === 'circle' && 'rounded-full',
            brand.logoShape === 'rounded' && 'rounded-md',
          )}
          style={{
            background: 'var(--form-accent-a11y,var(--form-accent,#6366F1))',
            color: 'var(--form-button-text,#FFFFFF)',
            fontSize: Math.max(12, Math.round(brand.logoSize * 0.34)),
            height: brand.logoSize,
            width: brand.logoSize,
          }}
        >
          {brand.logoText}
        </span>
      )}
      {brand.showBrandName && (
        <span className="min-w-0 truncate font-semibold tracking-normal">{brand.brandName}</span>
      )}
    </div>
  )

  return (
    <div
      className={cn(
        'pointer-events-none top-5 z-30 flex w-full px-5',
        contained ? 'absolute' : 'fixed',
        brand.logoPosition === 'top-center' && 'justify-center',
        brand.logoPosition === 'top-right' && 'justify-end',
        brand.logoPosition === 'top-left' && 'justify-start',
      )}
    >
      {brand.logoLinkUrl ? (
        <a
          className="pointer-events-auto"
          href={brand.logoLinkUrl}
          target="_blank"
          rel="noreferrer"
        >
          {content}
        </a>
      ) : (
        content
      )}
    </div>
  )
}

function BrandMark({
  brand,
  contained,
  progressPosition,
}: {
  brand: FormBrandSettings
  contained: boolean
  progressPosition: 'top' | 'bottom'
}) {
  const isSolid = brand.brandMarkStyle === 'solid'
  const content = (
    <span
      className={cn(
        'pointer-events-none inline-flex min-h-8 items-center rounded-full px-3 py-1.5 text-xs font-medium transition hover:opacity-100',
        brand.brandMarkStyle !== 'subtle' && 'border backdrop-blur',
      )}
      style={{
        background:
          brand.brandMarkStyle === 'subtle'
            ? 'transparent'
            : isSolid
              ? 'var(--form-button-a11y,var(--form-button,#6366F1))'
              : 'color-mix(in srgb, var(--form-bg,#F8FAFC) 82%, white)',
        borderColor:
          brand.brandMarkStyle === 'subtle'
            ? 'transparent'
            : 'color-mix(in srgb, var(--form-accent,#6366F1) 24%, transparent)',
        color: isSolid ? 'var(--form-button-text,#FFFFFF)' : 'var(--form-answer,#64748B)',
        fontFamily: 'var(--form-body-font,var(--form-font,Geist))',
        opacity: brand.brandMarkOpacity / 100,
      }}
    >
      {brand.brandMarkText}
    </span>
  )

  return (
    <div
      className={cn(
        'pointer-events-none z-30 flex w-full px-5',
        contained ? 'absolute' : 'fixed',
        progressPosition === 'bottom' ? 'bottom-5' : 'bottom-4',
        brand.brandMarkPosition === 'bottom-center' && 'justify-center',
        brand.brandMarkPosition === 'bottom-right' && 'justify-end',
        brand.brandMarkPosition === 'bottom-left' && 'justify-start',
      )}
    >
      {brand.brandMarkUrl ? (
        <a
          className="pointer-events-none"
          href={brand.brandMarkUrl}
          target="_blank"
          rel="noreferrer"
        >
          {content}
        </a>
      ) : (
        content
      )}
    </div>
  )
}
